/** @format */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, DOCUMENT, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { Observable } from 'rxjs';
import { map, retry, tap } from 'rxjs/operators';

import { KeyValue } from '@angular/common';

import { MetaService } from './meta.service';
import { HttpService, KillswitchService } from '@shared/data-access/services';
import { SeoDao } from '../dao/seo.dao';
import {
  Chain,
  Environment,
  SEOAttributes,
  SEOTabContent,
  SanitizedSEOAttributes,
  ProductListSEOResponse,
  SEOCategoryDetailsResponse,
  SEOCategoryDetails,
  PrunedProductListSEO,
  ProductListSEO,
  Breadcrumb,
} from '@shared/data-access/interfaces';
import { CustomHttpHeaders } from '@shared/data-access/enums';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly http = inject(HttpClient);
  private readonly chain = inject(Chain);
  private readonly environment = inject(Environment);
  private readonly seoDao = inject(SeoDao);
  private readonly document = inject(DOCUMENT) as HTMLDocument;
  private readonly metaService = inject(MetaService);
  private readonly title = inject(Title);
  private readonly httpService = inject(HttpService);
  private readonly killswitchService = inject(KillswitchService);
  private readonly isApiBrandDomainEnabled = this.killswitchService.killSwitchForCurrentChain(
    'enableSearchApiBrandDomain',
  );

  // Unlike SeoDao (already cut over unconditionally), this call path is still gated behind the flag
  private get SEO_URL(): string {
    return this.isApiBrandDomainEnabled()
      ? this.environment.api.seoByChain
      : this.environment.api.seo;
  }

  /**
   * @description
   * Builds and returns a default page meta description for a given product title and chain abbreviation
   */
  public getDefaultPageMetaDescription(productTitle: string, chainIdentifierAbbr: string): string {
    // Switch on the chain abbreviation to return a store specific meta description
    switch (chainIdentifierAbbr) {
      case 'calia':
        // Set the meta description
        return `Enjoy free shipping on ${productTitle} from CALIA. Read detailed descriptions and reviews on ${productTitle} and shop similar products at great prices.`;
      case 'gg':
        // Set the meta description
        return `Buy the ${productTitle} today and improve your game. Find product reviews and specs or compare products today at Golf Galaxy.`;
      case 'pl':
        // Set the meta description
        return `Shop ${productTitle} at Public Lands. Explore key product details to make sure you get the best fit for your needs.`;
      default:
        /*
         * DEFAULT TO DSG
         */
        // Set the meta description
        return `Shop a wide selection of ${productTitle} at DICK'S Sporting Goods and order online for the finest quality products from the top brands you trust.`;
    }
  }

  /**
   * @description
   * Builds and returns a default page title for a given product title and chain name
   */
  public getDefaultPageTitle(productTitle: string, chainName: string): string {
    // Return the default page title
    return `${productTitle} | ${chainName}`;
  }

  /**
   * @description
   * Trims the pipe and chain name off a page title if present
   */
  public getTrimmedPageTitle(pageTitle: string): string {
    // Return string
    let returnString = '';

    // Check to see if the argument is a string
    if (typeof pageTitle === 'string' && pageTitle.trim() !== '') {
      // Check to see if the title has the pipe
      returnString = pageTitle.includes('|')
        ? pageTitle.substring(0, pageTitle.indexOf('|')).trim()
        : pageTitle;
    }

    // Return the string
    return returnString;
  }

  // ******************** FUNCTION DECLARATIONS ********************//
  getSEOData$(
    seoToken: string,
    retries = 0,
    useDefaultHttpTransferCache = false,
    pageType = '',
  ): Observable<ProductListSEOResponse> {
    return this.seoDao.getSEOData$(seoToken, retries, useDefaultHttpTransferCache, pageType).pipe(
      map((response) => ({
        ...response,
        categories: response.categories.map((category) => ({
          ...category,
          sanitizedAttributes: this.getSeoAttributesMapped(category.attr),
        })),
      })),
    );
  }

  /**
   * Retrieves and caches the SEO category data for the given SEO token, with only the necessary subset of fields.
   * This is currently specifically meant for use by PLP.
   * A separate method is necessary for type safety until conditional return types are possible (see https://github.com/microsoft/TypeScript/issues/33912)
   */
  getPrunedSEOCategory$(seoToken: string, retries = 0, pageType = '') {
    return this.seoDao.getPrunedSEOCategory$(seoToken, retries, pageType).pipe(
      map((response): PrunedProductListSEO | null =>
        response
          ? {
              ...response,
              sanitizedAttributes: this.getSeoAttributesMapped(response.attr),
            }
          : null,
      ),
    );
  }

  private getSeoAttributesMapped(attributes: SEOAttributes): SanitizedSEOAttributes {
    return {
      seoFeaturedLinks: this.getSEOFeaturedLinksMapped(attributes),
      seoTabsContent: this.getSEOTabContentMapped(attributes),
      sdxIncludesSuppressFlag: this.getSEOAttributeValue(attributes, 'SDX_INCLUDES_SUPPRESS_FLAG'),
      seoRobotsNoIndex: this.getSEOAttributeValue(attributes, 'SEO_ROBOTS_NOINDEX'),
      seoKeywords: this.getSEOAttributeValue(attributes, 'SEO_KEYWORDS'),
      sponsoredProducts: this.getSEOAttributeValue(attributes, 'SPONSORED_PRODUCTS'),
      layoutType: this.getSEOAttributeValue(attributes, 'LAYOUT_TYPE'),
      pageType: this.getSEOAttributeValue(attributes, 'PAGE_TYPE'),
      metaDescription: this.getSEOAttributeValue(attributes, 'META_DESCRIPTION'),
      seoRobotsNoFollow: this.getSEOAttributeValue(attributes, 'SEO_ROBOTS_NOFOLLOW'),
      breadcrumb: this.getSEOAttributeValue(attributes, 'BREADCRUMB'),
      seoNotes: this.getSEOAttributeValue(attributes, 'SEO_NOTES'),
      seoHypercatSuppress: this.getSEOAttributeValue(attributes, 'SEO_HYPERCAT_SUPPRESS'),
      defaultLinkText: this.getSEOAttributeValue(attributes, 'DEFAULT_LINK_TEXT'),
      seoHeadline: this.getSEOAttributeValue(attributes, 'SEO_HEADLINE'),
      layoutPriority: this.getSEOAttributeValue(attributes, 'LAYOUT_PRIORITY'),
      topResultThumbnail: this.getSEOAttributeValue(attributes, 'TOP_RESULT_THUMBNAIL'),
      title: this.getSEOAttributeValue(attributes, 'TITLE'),
      seoCanonical: this.getSEOAttributeValue(attributes, 'SEO_CANONICAL'),
      layoutOption: this.getSEOAttributeValue(attributes, 'LAYOUT_OPTION'),
      topResultFullImage: this.getSEOAttributeValue(attributes, 'TOP_RESULT_FULLIMAGE'),
    };
  }

  private getSEOFeaturedLinksMapped(seoAttributes: SEOAttributes): KeyValue<string, string>[] {
    const featuredLinksResponse: KeyValue<string, string>[] = [];
    const featuredLinks = this.getSEOAttributeValue(seoAttributes, 'SEO_FEATURED_LINKS');

    if (featuredLinks) {
      const featuredLinksParse = JSON.parse(featuredLinks);
      Object.keys(featuredLinksParse).forEach((key) => {
        featuredLinksResponse.push({ key, value: featuredLinksParse[key] });
      });
    }

    return featuredLinksResponse;
  }

  private getSEOTabContentMapped(seoAttributes: SEOAttributes): SEOTabContent[] {
    const seoTabResultList: SEOTabContent[] = [];
    const attrHeadingMatchPattern = /\bSEO_TAB\d_HEADING/;

    Object.keys(seoAttributes).forEach((key) => {
      // For each key, check if it matches the heading match pattern.
      if (key.search(attrHeadingMatchPattern) === 0 && seoAttributes[key]?.value) {
        const header = seoAttributes[key].value;
        // Swap Tab header text with tab content text, to retrieve content data.
        const contentKey = key.replace('HEADING', 'CONTENT');
        if (seoAttributes[contentKey]?.value) {
          seoTabResultList.push({
            header,
            content: seoAttributes[contentKey].value,
          });
        }
      }
    });

    return seoTabResultList;
  }

  private getSEOAttributeValue(seoAttributes: SEOAttributes, key: string): string {
    return seoAttributes[key]?.value || '';
  }

  getsubCatSEOData$(seoToken: string, retries = 0): Observable<SEOCategoryDetailsResponse> {
    const requestUrl = `${this.SEO_URL}seo-category/v1/categories?seoUrl=${seoToken}&storeId=${this.chain.storeId}&children=true&published=true`;
    const headersConfig: Record<string, string> = {
      'X-TIMEOUT': '10000',
      'Content-Type': 'application/json',
    };

    // Get Origin Host Header
    const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
    if (origin) {
      headersConfig[CustomHttpHeaders.ORIGIN] = origin;
    }

    const httpOptions = {
      headers: new HttpHeaders(headersConfig),
      withCredentials: this.isApiBrandDomainEnabled(),
    };

    return this.http.get<SEOCategoryDetailsResponse>(requestUrl, httpOptions).pipe(
      map((response) => response),
      retry(retries),
    );
  }

  initialize(seoToken: string, path: string, retries = 0) {
    return this.getSEOData$(seoToken, retries).pipe(
      tap((result) => {
        this.initializePageSEO(result, path);
      }),
    );
  }

  private isSEOCategory(
    data: ProductListSEOResponse | PrunedProductListSEO,
  ): data is PrunedProductListSEO {
    return 'name' in data;
  }

  initializePageSEO(
    data: ProductListSEOResponse | PrunedProductListSEO,
    defaultSeoPath: string,
  ): void {
    const defaultCanonical = `${this.chain.metaCanonical}${defaultSeoPath}`;
    if (this.isSEOCategory(data) || data.categories?.[0]) {
      const seoData = this.isSEOCategory(data) ? data : data.categories[0];

      if (seoData.attr?.SEO_CANONICAL?.value) {
        this.addCanonicalTag(seoData.attr.SEO_CANONICAL.value, 'SEO Service');
      } else {
        this.addCanonicalTag(defaultCanonical);
      }

      this.createRobotsTag(
        seoData.attr.SEO_ROBOTS_NOINDEX,
        seoData.attr.SEO_ROBOTS_NOFOLLOW,
        seoData.attr.SEO_ROBOTS_FOLLOW,
      );

      this.setPageMetaInformation(seoData);
    } else {
      this.addCanonicalTag(defaultCanonical);
      this.metaService.addTag(
        {
          property: 'og:url',
          content: defaultCanonical,
        },
        true,
      );
    }
  }

  createRobotsTag(
    noindex: { value?: string },
    nofollow: { value?: string },
    follow: { value?: string },
  ) {
    const robotsParams = [];

    if (noindex?.value?.toUpperCase() === 'Y') {
      robotsParams.push('noindex');
    }

    if (nofollow?.value?.toUpperCase() === 'Y') {
      robotsParams.push('nofollow');
    }

    if (follow?.value?.toUpperCase() === 'Y') {
      robotsParams.push('follow');
    }

    if (robotsParams.length > 0) {
      const content = robotsParams.join(',');
      this.metaService.addTag({ name: 'robots', content }, true);
    }
  }

  setPageMetaInformation(data: PrunedProductListSEO | ProductListSEO) {
    const title = data.title || this.getDefaultPageTitle(data.name, this.chain.chainName);
    const description =
      data.metaDesc ||
      this.getDefaultPageMetaDescription(data.name, this.chain.chainIdentifierAbbr);
    const keywords = data.metaKeyword ? [{ name: 'keywords', content: data.metaKeyword }] : [];

    this.title.setTitle(title);
    this.metaService.addTags(
      [
        {
          name: 'apple-mobile-web-app-title',
          content: title,
        },
        {
          name: 'description',
          content: description,
        },
        ...keywords,
        {
          property: 'og:type',
          content: 'category',
        },
        {
          property: 'og:site_name',
          content: this.chain.chainName,
        },
        {
          property: 'og:title',
          content: title.includes('|') ? title.substring(0, title.indexOf('|')).trim() : title,
        },
        {
          property: 'og:description',
          content: description,
        },
      ],
      true,
    );
  }

  setSearchAction(storeURL: string) {
    const scriptTag = document.createElement('script');

    scriptTag.type = 'application/ld+json';
    scriptTag.text = `{
			"@context": "http://schema.org",
			"@type": "WebSite",
			"url": "${storeURL}",
			"potentialAction": {
				"@type": "SearchAction",
				"target":  "${storeURL}/search/SearchDisplay?searchTerm={search_term_string}",
				"query-input": {
					"@type": "PropertyValueSpecification",
					"valueRequired": true,
					"valueMaxlength": 100,
					"valueName": "search_term_string"
				}
			}
		}`;

    document.head.appendChild(scriptTag);
  }

  addCanonicalTag(url: string, comment = 'Default') {
    const canonicalLink: HTMLLinkElement | null =
      this.document.head.querySelector('[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', url);
      if (canonicalLink.dataset) {
        canonicalLink.dataset.comment = `${comment} Canonical`;
      }
    } else {
      // Create a link element
      const canonicalLink = this.document.createElement('link');

      // Set the link relationship attribute
      canonicalLink.setAttribute('rel', 'canonical');

      // Set the link href attribute
      canonicalLink.setAttribute('href', url);

      if (canonicalLink.dataset) {
        canonicalLink.dataset.comment = `${comment} Canonical`;
      }

      // Append the link element to the head element
      this.document.head.appendChild(canonicalLink);
    }
  }

  /**
   * @description
   * Accepts fullImage and returns updated imagePath
   */
  getimagePath(category: SEOCategoryDetails) {
    if (category.thumbnail) {
      if (category.thumbnail.includes('Attachment/')) {
        return `${this.chain.metaCanonical}/wcsstore/${this.chain.chainIdentifier}/${category.thumbnail}`;
      }
      // eslint-disable-next-line no-useless-escape
      const thumbnailName = category.thumbnail.replaceAll(/[\/ -]/g, '_');
      return `${this.environment.thirdParty.scene7.url}${thumbnailName}/?${this.environment.thirdParty.scene7.suffix}`;
    }

    if (category.attr?.['TOP_RESULT_THUMBNAIL']) {
      // eslint-disable-next-line no-useless-escape
      const ecode = category.attr['TOP_RESULT_THUMBNAIL'].value.replaceAll(/[\/ -]/g, '_');
      return `${this.environment.thirdParty.scene7.url}${ecode}/?${this.environment.thirdParty.scene7.suffix}`;
    }

    return '';
  }

  /**
   * Sets `_dsgTag.categoryList` and fires the `CategoryListLoaded` event for Header/Footer.
   * Should only be fired client-side, in-browser.
   * @param breadCrumbDetails - The breadcrumb details from the SEO response
   */
  handleCategoryListLoaded(breadCrumbDetails: Breadcrumb[] | undefined) {
    // Default to empty string if breadCrumbDetails is undefined
    const categoryList = (breadCrumbDetails ?? []).map((breadcrumb) => breadcrumb.name).join('|');
    const categoryListLoadedEvent = new CustomEvent('CategoryListLoaded', {
      detail: { categoryList },
    });
    globalThis.document.dispatchEvent(categoryListLoadedEvent);
    if (globalThis._dsgTag) {
      globalThis._dsgTag.categoryList = categoryList;
    }
  }
}
